import type { FC, ReactNode } from 'react';
import { Fragment, useMemo, useEffect } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import {
  BiData,
  SignupScreenClickContinueBiEvent,
} from '@wix/da-bi/pkg/events';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { SocialLoginProvider } from '@wix/da-shared-react/pkg/SocialLogin/types';
import SocialButtonApple from '@wix/da-shared-react/pkg/SocialLogin/SocialButtonApple';
import SocialButtonFacebook from '@wix/da-shared-react/pkg/SocialLogin/SocialButtonFacebook';
import SocialButtonGoogle from '@wix/da-shared-react/pkg/SocialLogin/SocialButtonGoogle';
import PasswordInput from '../PasswordInput';
import JoinState from '../../../types/join';
import SocialLoginDivider from '../SocialLoginDivider';
import { getAuthMode } from '../../../helpers/bi';
import UsernameForm from '../UsernameForm';
import CompleteRedirectForm from '../CompleteRedirectForm';

import s from './JoinForm.scss';

export interface Props extends JoinState {
  changePassword(password: string): void;
  changeEmail(email: string): void;
  validateEmail(email: string): void;
  joinWithEmail(): void;
  errorDisplayed?(errorMessage): void;
}

const JoinForm: FC<Props> = ({
  email = '',
  emailError,
  joinMode,
  password = '',
  passwordError,
  passwordStrength,
  changePassword,
  changeEmail,
  validateEmail,
  errorDisplayed,
  joinWithEmail,
}) => {
  const { t, i18n } = useTranslation();

  const strength = (password && passwordStrength) || undefined;
  const errors = useMemo(
    () => ({
      email: emailError && (
        <>
          {t([`join.error.email.${emailError}`, 'join.error.email.invalid'])}{' '}
          {i18n.exists(`join.error.email.${emailError}.link.href`) && (
            <BiLink
              href={t(`join.error.email.${emailError}.link.href`)}
              className={s['error-link']}
            >
              {t(`join.error.email.${emailError}.link.label`)}
            </BiLink>
          )}
        </>
      ),
      password: getPasswordErrorDetails(passwordError, strength, t),
    }),
    [t, i18n, emailError, strength, passwordError]
  );

  useEffect(() => {
    if (passwordError === 'similar_username' && errorDisplayed) {
      errorDisplayed('similar_username');
    }
    if (emailError === 'invalid' && errorDisplayed) {
      errorDisplayed('email_invalid');
    }
    if (emailError === 'unavailable' && errorDisplayed) {
      errorDisplayed('email_banned');
    }
  }, [passwordError, emailError, errorDisplayed]);

  if (joinMode) {
    return <UsernameForm />;
  }

  return (
    (<div className={s['root']}>
      <Fragment key="social">
        <div className={s['social-buttons']}>
          <SocialButtonGoogle
            className={s['social-button']}
            biData={BiData<SignupScreenClickContinueBiEvent>({
              evid: 238,
              page_number: 1,
              auth_mode: getAuthMode(SocialLoginProvider.Google),
            })}
          />
          <SocialButtonApple
            className={s['social-button']}
            biData={BiData<SignupScreenClickContinueBiEvent>({
              evid: 238,
              page_number: 1,
              auth_mode: getAuthMode(SocialLoginProvider.Apple),
            })}
          >
            {t('join.form.button.continue.apple')}
          </SocialButtonApple>
          <SocialButtonFacebook
            className={s['social-button']}
            biData={BiData<SignupScreenClickContinueBiEvent>({
              evid: 238,
              page_number: 1,
              auth_mode: getAuthMode(SocialLoginProvider.Facebook),
            })}
          >
            {t('join.form.button.continue.facebook')}
          </SocialButtonFacebook>
        </div>
        <CompleteRedirectForm />
      </Fragment>
      <SocialLoginDivider key="divider" className={s['divider']} />
      <Fragment key="form">
        <div className={c(s['email'], emailError && s['field-error'])}>
          <TextInput
            id="email"
            name="email"
            label={t('join.form.email')}
            value={email}
            validationResult={errors.email ? 'error' : undefined}
            details={errors.email}
            autoFocus
            noBottomSpacing
            spellCheck={false}
            autoCapitalize="none"
            onChange={ev => changeEmail(ev.target.value.trim())}
            onBlur={() => {
              validateEmail(email);
            }}
          />
        </div>
        <div
          className={c(s['password'], !!errors.password && s['field-error'])}
        >
          <PasswordInput
            id="password"
            name="password"
            noBottomSpacing
            label={t('join.form.password')}
            className={s['password']}
            value={password}
            strength={strength}
            onChange={ev => changePassword(ev.target.value)}
            validationResult={errors.password ? 'error' : undefined}
            details={errors.password}
            autoComplete="new-password"
          />
        </div>

        <div className={s['password-hint']}>{t('join.form.password.hint')}</div>

        <BrandedButton
          // id="joinbutton"
          className={s['button']}
          size="large"
          variant="brand-green"
          type="button"
          onClick={joinWithEmail}
          fillContainerWidth
        >
          {t('join.form.button.continue.email')}
        </BrandedButton>
      </Fragment>
    </div>)
  );
};
JoinForm.displayName = 'JoinForm';

export default JoinForm;

/** Returns an error string for a weak password or server error,
 *  or false if no error  */
function getPasswordErrorDetails(
  errorFromServer: string | null | undefined,
  strength: string | undefined,
  t: (a: any) => string
): boolean | ReactNode {
  const passwordRefusedAtServer = !!(errorFromServer && !strength);
  const passwordTooWeak = strength === 'weak';
  const shouldShowPasswordError = passwordRefusedAtServer || passwordTooWeak;
  return (
    shouldShowPasswordError &&
    t(
      passwordTooWeak
        ? 'join.form.password.hint'
        : [`join.error.password.${errorFromServer}`, 'join.form.password.hint']
    )
  );
}
